import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { TopBar } from '../../contasis/layout/TopBar';
import { Sidebar } from '../../contasis/layout/Sidebar';
import { useAuth } from '../../contexts/ContextoAuth';
import { useWorkspace } from '../../contexts/ContextoWorkspace';
import { useToast } from '../../contexts/ContextoToast';
import { useSidebar } from '../../contexts/ContextoSidebar';
import { useNetworkStatus } from '../../hooks/useNetworkStatus';
import { useSessionMonitor } from '../../hooks/useSessionMonitor';
import { usePageTitle } from '../../hooks/usePageTitle';
import { ROUTES } from '../../utils/constants';
import { Dialog } from '@/contasis';
import { SessionExpirationModal } from '../../components/SessionExpirationModal';
import type { Module } from '../../contasis';
import { useCaja } from '../../contexts/CajaContext';
import { calcularResumenEfectivoTotal } from '../../utils/cajas/calculations';
import { useComprobanteContext } from '@/features/Comprobantes/contexts/ComprobantesListContext';
import type { CajaData } from '../../contasis/layout/CajaStatus';
import { DemoBanner } from '../../components/DemoBanner/DemoBanner';
import { DemoPublicaBanner } from '../../components/DemoBanner/DemoPublicaBanner';
import { HabilitacionPseBanner } from '../../components/HabilitacionPseBanner/HabilitacionPseBanner';
import { obtenerRegistroHabilitacionPseMock, suscribirseAHabilitacionPseMock } from '@/shared/habilitacionPseMock';
import { esEmpresaDemo } from '@/shared/demo';
import { SuperAdminBanner } from '../../components/SuperAdminBanner/SuperAdminBanner';
//import { OnboardingModal } from '../../components/Onboarding/OnboardingModalMVP1';
import { OnboardingModal } from '../../components/Onboarding/OnboardingModal';
import { PaletaComandos } from '../../components/PaletaComandos/PaletaComandos';
import { KeyboardShortcutsModal } from '../../components/KeyboardShortcuts/KeyboardShortcutsModal';
import { ChecklistInicioMount } from '@/features/checklist-inicio/components/ChecklistInicioMount';
import { NotificacionesProvider } from '../../contexts/ContextoNotificaciones';
import { NotificacionesDropdown } from '../../components/Notificaciones/NotificacionesDropdown';
import { AnalyticsIdentitySync } from '../../shared/analitica/AnalyticsIdentitySync';
import { registrarCierreSesionExitoso } from '../../shared/analitica/analitica';
import { useRetroalimentacion } from '../../shared/retroalimentacion';
import { usarAyudaGuiada, obtenerTour } from '../../shared/tour';
import { useOnboarding } from '../../hooks/useOnboardingMVP1';
import { useVentanasIniciales } from '../../hooks/useVentanasIniciales';
import { useComandosTeclado } from '../../hooks/useComandosTeclado';
import { storageService } from '../../utils/storage';
import { useSuscripcion } from '../../hooks/useSuscripcion';
import { construirPlanStatusCard } from '../../utils/planStatusCard';
import { construirUrlSuscripcion } from '../../utils/enlacesBilling';
import { suscripcionesService } from '../../services/api/suscripciones.service';

/**
 * Layout principal para páginas privadas/autenticadas
 * Incluye TopBar (Header), Sidebar y área de contenido
 */
const PrivateLayout = () => {
  usePageTitle();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, logout, refreshSession } = useAuth();
  const { empresas, establecimientos, empresaId, establecimientoId, currentEmpresa, setWorkspace, refreshFromStorage } = useWorkspace();
  const [, forzarRefrescoHabilitacionPse] = useState(0);
  useEffect(() => suscribirseAHabilitacionPseMock(() => forzarRefrescoHabilitacionPse((n) => n + 1)), []);
  const facturacionElectronicaEstado: 'pendiente' | 'enProceso' | 'habilitada' | undefined =
    currentEmpresa && !esEmpresaDemo(currentEmpresa.ruc)
      ? obtenerRegistroHabilitacionPseMock(currentEmpresa.ruc)?.estado ?? 'pendiente'
      : undefined;
  const { showToast } = useToast();
  const { isOnline, wasOffline } = useNetworkStatus();
  const { isExpiringSoon, timeRemaining, dismissWarning, resetWarning } = useSessionMonitor(isAuthenticated);
  const { isSidebarOpen, toggleSidebar, isTopBarVisible } = useSidebar();
  const { estaTourCompletado } = usarAyudaGuiada();
  const { status: cajaStatus, aperturaActual, activeCajaNombre, activeCajaId, cajasDisponibles, setActiveCaja, getResumen, movimientos } = useCaja();
  const { paginacion } = useComprobanteContext();
  const [activeModule, setActiveModule] = useState('dashboard');
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showLogoutCajaAbiertaModal, setShowLogoutCajaAbiertaModal] = useState(false);
  const { mostrar: onboardingPendiente, marcarVisto: marcarOnboardingVisto } = useOnboarding(user?.id);
  const { mostrarBienvenida, ventanasPendientes } = useVentanasIniciales();
  // La bienvenida espera a que terminen régimen y contraseña.
  const showOnboarding = onboardingPendiente && mostrarBienvenida;
  // TEMPORAL: solo para previsualizar el diseño del onboarding sin crear cuentas nuevas. Quitar al terminar.
  const [showOnboardingPreview, setShowOnboardingPreview] = useState(false);
  const handleOnboardingClose = useCallback(() => {
    if (showOnboarding) marcarOnboardingVisto();
    setShowOnboardingPreview(false);
  }, [showOnboarding, marcarOnboardingVisto]);
  const { abrirPanel: abrirPanelRetroalimentacion } = useRetroalimentacion();
  const [showPaletaComandos, setShowPaletaComandos] = useState(false);
  const [showKeyboardShortcuts, setShowKeyboardShortcuts] = useState(false);

  useEffect(() => {
    if (user?.email) {
      (window as Window & { rybbit?: { identify: (email: string) => void } }).rybbit?.identify(user.email);
    }
  }, [user?.email]);

  useComandosTeclado(() => setShowPaletaComandos(true));

  const suscripcion = useSuscripcion();
  const urlElegirPlan = useMemo(
    () => construirUrlSuscripcion(empresaId, {
      view: 'pago',
      sku: suscripcion?.planSku ?? undefined,
      planSku: suscripcion?.planSku ?? undefined,
    }),
    [empresaId, suscripcion?.planSku],
  );
  const esCuentaDemoAccesoDirecto = user?.esCuentaDemo ?? false;

  const planStatus = useMemo(
    () => {
      if (esCuentaDemoAccesoDirecto) {
        return {
          plan: 'demo' as const,
          planNombre: 'Senciyo SAC',
          estado: 'demoPublica' as const,
          onAccion: () => {
            navigate('/register', { replace: true });
            void logout();
          },
        };
      }

      const urlGestionar = construirUrlSuscripcion(empresaId, { planSku: suscripcion?.planSku ?? undefined });
      return construirPlanStatusCard(suscripcion, {
        onVerPlanes: () => window.open(urlElegirPlan, '_blank'),
        onGestionar: () => window.open(urlGestionar, '_blank'),
        onCrearEmpresa: () => navigate('/configuracion/empresa/create'),
      });
    },
    [suscripcion, empresaId, urlElegirPlan, esCuentaDemoAccesoDirecto, logout, navigate],
  );

  const tourModuloActual = useMemo(() => {
    if (location.pathname.includes('comprobantes/emision')) {
      return { id: 'primera-venta', nombre: 'Nuevo comprobante' };
    }
    return null;
  }, [location.pathname]);

  const [tourProgreso, setTourProgreso] = useState<{ pasoActual: number; totalPasos: number } | undefined>();

  useEffect(() => {
    if (!tourModuloActual) {
      setTourProgreso(undefined);
      return;
    }
    const definicion = obtenerTour(tourModuloActual.id);
    const totalPasos = definicion?.pasos.length ?? 0;
    const completado = estaTourCompletado(tourModuloActual.id, definicion?.version ?? 1);
    setTourProgreso({ pasoActual: completado ? totalPasos - 1 : -1, totalPasos });
  }, [tourModuloActual, estaTourCompletado]);

  useEffect(() => {
    const handleProgreso = (e: Event) => {
      const detail = (e as CustomEvent).detail as { tourId: string; indicePaso: number; totalPasos: number } | null;
      if (!tourModuloActual) return;
      if (!detail) {
        const definicion = obtenerTour(tourModuloActual.id);
        const version = definicion?.version ?? 1;
        if (estaTourCompletado(tourModuloActual.id, version)) {
          setTourProgreso({ pasoActual: (definicion?.pasos.length ?? 1) - 1, totalPasos: definicion?.pasos.length ?? 0 });
        }
        // Si fue cerrado/saltado sin completar, conservar el último avance
        return;
      }
      if (detail.tourId !== tourModuloActual.id) return;
      setTourProgreso({ pasoActual: detail.indicePaso, totalPasos: detail.totalPasos });
    };
    window.addEventListener('senciyo:tour-progreso', handleProgreso);
    return () => window.removeEventListener('senciyo:tour-progreso', handleProgreso);
  }, [tourModuloActual, estaTourCompletado]);

  // Detectar cambios de red
  useEffect(() => {
    if (!isOnline) {
      showToast('⚠️ Sin conexión a internet', 'warning');
    }
  }, [isOnline, showToast]);

  useEffect(() => {
    if (wasOffline && isOnline) {
      showToast('✓ Conexión restaurada', 'success');
    }
  }, [wasOffline, isOnline, showToast]);

  useEffect(() => {
    const handleStorageChange = () => {
      refreshFromStorage();
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [refreshFromStorage]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate(ROUTES.PUBLIC.LOGIN, { replace: true });
      return;
    }

    if (!empresaId && empresas.length > 0) {
      navigate(ROUTES.PRIVATE.SELECT_WORKSPACE, { replace: true });
    }
  }, [isAuthenticated, empresaId, empresas.length, navigate]);

  const pagoExitosoProcesadoRef = useRef(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('pago') !== 'exitoso' || pagoExitosoProcesadoRef.current) return;
    pagoExitosoProcesadoRef.current = true;

    const empresaIdPago = params.get('empresaId');

    params.delete('pago');
    params.delete('empresaId');
    const queryRestante = params.toString();
    navigate(`${location.pathname}${queryRestante ? `?${queryRestante}` : ''}`, { replace: true });

    if (!empresaIdPago) return;

    if (empresaIdPago !== empresaId && empresas.some(e => e.id === empresaIdPago)) {
      const establecimientosEmpresa = establecimientos.filter(e => e.empresaId === empresaIdPago);
      const establecimientoPrincipal = establecimientosEmpresa.find(e => e.esPrincipal);
      const establecimientoAuto = establecimientoPrincipal
        ?? (establecimientosEmpresa.length === 1 ? establecimientosEmpresa[0] : undefined);
      setWorkspace(empresaIdPago, establecimientoAuto?.id);
    }

    void suscripcionesService.cargarLote([empresaIdPago], true).then(() => {
      showToast('Tu suscripción está activa. Ya puedes usar el sistema con normalidad.', 'success');
    });
  }, [location.pathname, location.search, navigate, showToast, empresaId, empresas, establecimientos, setWorkspace]);

  // Sincronizar activeModule con la ruta actual
  useEffect(() => {
    const path = location.pathname;
    
    // Extraer el primer segmento de la ruta después de '/'
    const match = path.match(/^\/([^/]+)/);
    if (match) {
      const moduleId = match[1];
      // Solo actualizar si el módulo existe en la lista de módulos
      const validModules = ['comprobantes', 'guias', 'punto-venta', 'documentos-comerciales', 'productos', 'inventarios', 'precios', 'cajas', 'compras', 'gastos', 'cobranzas', 'clientes', 'indicadores'];
      if (validModules.includes(moduleId)) {
        setActiveModule(moduleId);
      } else {
        // Para rutas que no son módulos del sidebar (como configuración, administrar-empresas, etc.)
        // limpiar la selección
        setActiveModule('');
      }
    }
  }, [location.pathname]);

  const comprobantesCount = paginacion.total;

  const modules: Module[] = useMemo(() => {
    const permisos = storageService.getPermisos();
    const tiene = (codigo: string) =>
      permisos.length === 0 || permisos.some(p => p.codigo === codigo && p.esActivo);

    return [
      {
        id: "comprobantes",
        title: "Comprobantes",
        icon: "FileText",
        ...(comprobantesCount > 0 && { badge: String(comprobantesCount) }),
        visible: tiene('ventas.ver_comprobantes'),
      },
      {
        id: "guias",
        title: "Guías de Remisión",
        icon: "Folder",
        visible: tiene('ventas.ver_comprobantes'),
      },
      {
        id: "punto-venta",
        title: "Punto de Venta",
        icon: "ShoppingCart",
      },
      {
        id: "documentos-comerciales",
        title: "Doc. Comerciales",
        icon: "FileStack",
        visible: true,
      },
      {
        id: "cobranzas",
        title: "Cobranzas",
        icon: "Coins",
        visible: tiene('cobranzas.ver'),
      },
      {
        id: "compras",
        title: "Compras",
        icon: "Truck",
        visible: true,
      },
      {
        id: "gastos",
        title: "Gastos",
        icon: "TrendingDown",
        visible: true,
      },
      {
        id: "inventarios",
        title: "Inventario",
        icon: "Boxes",
      },
      {
        id: "productos",
        title: "Productos",
        icon: "Package",
        visible: tiene('catalogo.ver'),
      },
      {
        id: "precios",
        title: "Precios",
        icon: "DollarSign",
      },
      {
        id: "clientes",
        title: "Clientes",
        icon: "Users",
        visible: tiene('clientes.ver'),
      },
      {
        id: "cajas",
        title: "Cajas",
        icon: "Wallet",
        visible: tiene('caja.ver'),
      },
      {
        id: "indicadores",
        title: "Indicadores",
        icon: "BarChart3",
        visible: tiene('indicadores.ver'),
      },
    ];
  }, [comprobantesCount]);

  const userData = useMemo(() => user ? {
    name: `${user.nombre} ${user.apellido}`.trim(),
    email: user.email,
    role: user.esSuperAdminSenciyo ? 'Superadmin' : 'Usuario',
    avatar: user.fotoUrl ?? undefined,
  } : {
    name: 'Usuario',
    email: '',
    role: 'Usuario',
    avatar: undefined,
  }, [user]);

  const empresasFormateadas = useMemo(() => empresas
    .filter(empresa => empresa.esActivo)
    .map(empresa => ({
      id: empresa.id,
      nombre: empresa.razonSocial,
      ruc: empresa.ruc,
    })), [empresas]);

  const sedesFormateadas = useMemo(() => establecimientos.map(est => ({
    id: est.id,
    nombre: est.nombre,
    direccion: est.direccion || est.codigo,
    empresaId: est.empresaId,
    esActivo: est.esActivo,
  })), [establecimientos]);

  const cajaData = useMemo((): CajaData | undefined => {
    const resumen = getResumen();
    const resumenEfectivo = calcularResumenEfectivoTotal(movimientos);
    return {
      numero: activeCajaNombre ?? '',
      abierta: cajaStatus === 'abierta',
      apertura: {
        fecha: aperturaActual?.fechaHoraApertura ?? new Date(),
        hora: aperturaActual
          ? new Date(aperturaActual.fechaHoraApertura).toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit' })
          : '--:--',
        usuario: aperturaActual?.usuarioNombre ?? '',
        monto: aperturaActual?.montoInicialTotal ?? 0,
      },
      ingresos: resumenEfectivo.ingresos,
      saldo: resumen.apertura + resumenEfectivo.ingresos - resumenEfectivo.egresos,
    };
  }, [cajaStatus, aperturaActual, getResumen, movimientos]);

  const handleVerMovimientosCaja = useCallback(() => {
    navigate('/cajas?tab=movimientos');
  }, [navigate]);

  const handleCerrarCaja = useCallback(() => {
    navigate('/cajas?tab=cierre');
  }, [navigate]);

  const handleAbrirCaja = useCallback(() => {
    navigate('/cajas?tab=apertura');
  }, [navigate]);

  const handleModuleChange = useCallback((moduleId: string) => {
    setActiveModule(moduleId);
    navigate(`/${moduleId}`);
  }, [navigate]);

  const handleCrearDocumento = useCallback((tipo: string) => {
    // Navegar a la vista de emision con el tipo de comprobante
    navigate(`/comprobantes/emision?tipo=${tipo}`);
  }, [navigate]);

  const handleChangeWorkspace = useCallback((newEmpresaId: string, newSedeId: string) => {
    const empresa = empresas.find(e => e.id === newEmpresaId);
    const establecimiento = establecimientos.find(e => e.id === newSedeId);
    
    if (!empresa || !establecimiento) {
      showToast('Error al cambiar workspace', 'error');
      return;
    }

    setWorkspace(newEmpresaId, newSedeId);
    showToast(`Empresa: ${empresa.razonSocial} y Establecimiento: ${establecimiento.nombre} seleccionados`, 'success');
  }, [empresas, establecimientos, setWorkspace, showToast]);

  const handleSettings = useCallback(() => {
    navigate('/configuracion');
  }, [navigate]);

  const handleProfile = useCallback(() => {
    navigate('/perfil');
  }, [navigate]);

  const handleLogout = useCallback(() => {
    if (cajaStatus === 'abierta') {
      setShowLogoutCajaAbiertaModal(true);
    } else {
      setShowLogoutModal(true);
    }
  }, [cajaStatus]);

  const handleConfirmLogout = useCallback(async (motivo: 'manual' | 'expiracion_sesion' | 'renovacion_fallida' | 'otro' = 'manual') => {
    setShowLogoutModal(false);
    try {
      registrarCierreSesionExitoso({ motivo, origen: 'private_layout' });
      await logout();
      showToast('¡Hasta pronto!', 'success');
    } catch {
      showToast('Sesión cerrada', 'warning');
    }
  }, [logout, showToast]);

  const handleContinueSession = useCallback(async () => {
    try {
      const refreshed = await refreshSession();
      if (refreshed) {
        showToast('Sesión renovada exitosamente', 'success');
        resetWarning();
        return;
      }

      if (!storageService.getRefreshToken()) {
        showToast('Error al renovar la sesión', 'error');
        dismissWarning();
        setTimeout(() => {
          handleConfirmLogout('renovacion_fallida');
        }, 2000);
        return;
      }

      showToast('No se pudo conectar con el servidor. Intenta nuevamente.', 'error');
    } catch {
      showToast('No se pudo conectar con el servidor. Intenta nuevamente.', 'error');
    }
  }, [refreshSession, showToast, resetWarning, dismissWarning, handleConfirmLogout]);

  const handleSessionExpiredLogout = useCallback(async () => {
    dismissWarning();
    await handleConfirmLogout('expiracion_sesion');
  }, [dismissWarning, handleConfirmLogout]);

  return (
    <NotificacionesProvider>
      <AnalyticsIdentitySync />
    <div className="h-screen supports-[height:100dvh]:h-dvh flex flex-col bg-slate-50 dark:bg-gray-900 overflow-hidden print:block print:h-auto print:min-h-0 print:bg-white print:overflow-visible" style={{ '--sidebar-w': isSidebarOpen ? 'var(--sb-width-open)' : 'var(--sb-width-collapsed)' } as React.CSSProperties}>
      {esCuentaDemoAccesoDirecto && (
        <div className="flex-shrink-0 print:hidden">
          <DemoPublicaBanner
            onRegistrar={() => {
              navigate('/register', { replace: true });
              void logout();
            }}
          />
        </div>
      )}

      {/* Header / TopBar */}
      {isTopBarVisible && <TopBar
        onToggleSidebar={toggleSidebar}
        showCaja={true}
        cajaData={cajaData}
        cajasDisponibles={cajasDisponibles}
        cajaSeleccionadaId={activeCajaId ?? undefined}
        onCambiarCaja={setActiveCaja}
        empresas={empresasFormateadas}
        sedes={sedesFormateadas}
        initialEmpresaId={empresaId || undefined}
        initialSedeId={establecimientoId || undefined}
        user={userData}
        onChangeWorkspace={handleChangeWorkspace}
        onCrearDocumento={handleCrearDocumento}
        onCrearCliente={() => navigate('/clientes?action=create')}
        onCrearProducto={() => navigate('/productos?action=create')}
        onVerMovimientosCaja={handleVerMovimientosCaja}
        onCerrarCaja={handleCerrarCaja}
        onAbrirCaja={handleAbrirCaja}
        onSettings={handleSettings}
        settingsActive={location.pathname.startsWith('/configuracion')}
        notificacionesSlot={<NotificacionesDropdown />}
        onLogout={handleLogout}
        facturacionElectronicaEstado={facturacionElectronicaEstado}
        onElectronicInvoicingClick={() => navigate('/perfil/habilitaciones')}
        onProfileClick={handleProfile}
        onLogoClick={() => navigate('/dashboard')}
        onFeedback={() => abrirPanelRetroalimentacion()}
        onSubscriptionsClick={() => navigate('/perfil/suscripciones')}
        onShowKeyboardShortcuts={() => setShowKeyboardShortcuts(true)}
        planStatus={planStatus}
        onOpenCommandPalette={() => setShowPaletaComandos(true)}
        tourModulo={tourModuloActual}
        tourProgreso={tourProgreso}
        onAdministrarEmpresas={() => navigate('/administrar-empresas')}
        onSearchNavigate={(route, term) => navigate(route, term ? { state: { search: term } } : undefined)}
      />}

      <SuperAdminBanner />
      {!esCuentaDemoAccesoDirecto && <DemoBanner onRegistrar={() => window.open(urlElegirPlan, '_blank')} />}
      <HabilitacionPseBanner onVerHabilitacion={() => navigate('/perfil/habilitaciones', { state: { autoHabilitar: true } })} />

      <div className="flex flex-1 overflow-hidden print:block print:overflow-visible">
        <div className={`flex-shrink-0 h-full relative z-50 print:hidden ${!isTopBarVisible ? 'hidden' : ''}`}>
          <Sidebar
            isOpen={isSidebarOpen}
            modules={modules}
            activeModule={activeModule}
            onModuleChange={handleModuleChange}
            theme="light"
          />
        </div>

        <main className='flex-1 flex flex-col transition-all duration-300 ease-in-out overflow-x-hidden bg-surface-1 print:block print:overflow-visible'>
          <Outlet />
        </main>
      </div>

      {showLogoutModal && (
        <Dialog
          type="error"
          title="Cerrar sesión"
          onClose={() => setShowLogoutModal(false)}
          onConfirm={handleConfirmLogout}
          confirmText="Cerrar sesión"
          cancelText="Cancelar"
        >
          ¿Estás seguro que deseas cerrar tu sesión?
        </Dialog>
      )}

      {showLogoutCajaAbiertaModal && (
        <Dialog
          type="warning"
          title="Confirmar cierre de sesión"
          onClose={() => setShowLogoutCajaAbiertaModal(false)}
          onConfirm={() => {
            setShowLogoutCajaAbiertaModal(false);
            void handleConfirmLogout();
          }}
          confirmText="Cerrar sesión"
          cancelText="Cancelar"
        >
          Tiene una caja abierta. ¿Desea cerrar sesión sin realizar el cierre de caja?
        </Dialog>
      )}

      <OnboardingModal
        isOpen={showOnboarding || showOnboardingPreview}
        onClose={handleOnboardingClose}
        onRegister={() => { handleOnboardingClose(); navigate('/configuracion/empresa/create'); }}
        esCreadoPorApiKey={user?.esCreadoPorApiKey}
        onFinalizar={() => { handleOnboardingClose(); navigate('/comprobantes/emision?tipo=03'); }}
      />

      {false && import.meta.env.DEV && ( // Oculto a pedido del usuario, reactivar cuando lo pida
        <button
          type="button"
          onClick={() => setShowOnboardingPreview(true)}
          style={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            zIndex: 9999,
            padding: '8px 14px',
            borderRadius: 8,
            border: 'none',
            background: '#7c3aed',
            color: '#fff',
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
          }}
        >
          🔧 Ver Onboarding
        </button>
      )}

      <PaletaComandos
        isOpen={showPaletaComandos}
        onClose={() => setShowPaletaComandos(false)}
      />

      <KeyboardShortcutsModal
        isOpen={showKeyboardShortcuts}
        onClose={() => setShowKeyboardShortcuts(false)}
      />

      <ChecklistInicioMount
        isModalOpen={
          showLogoutModal ||
          showLogoutCajaAbiertaModal ||
          ventanasPendientes ||
          showOnboardingPreview ||
          showPaletaComandos ||
          showKeyboardShortcuts
        }
        onNotify={(mensaje) => showToast(mensaje, 'info')}
      />

      <SessionExpirationModal
        isOpen={isExpiringSoon}
        timeRemaining={timeRemaining}
        onContinue={handleContinueSession}
        onLogout={handleSessionExpiredLogout}
      />
    </div>
    </NotificacionesProvider>
  );
};

export default PrivateLayout;